/**
 * Integration Map API Client
 *
 * Typed client for the functional integration map endpoints.
 * Uses authFetch from AuthContext for authenticated requests.
 *
 * WQ-099: Phase 4.6.1 Executive Views
 */

// ── Types matching backend camelCase responses ──────────────────────

export interface IntegrationNode {
  id: string;
  tenantId: string;
  areaKey: string;
  label: string;
  description: string | null;
  icon: string;
  color: string;
  xPosition: number | null;
  yPosition: number | null;
  healthStatus: 'healthy' | 'warning' | 'critical' | 'unknown';
  healthUpdatedAt: string | null;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
  edgeCount: number;
}

export interface IntegrationEdge {
  id: string;
  tenantId: string;
  sourceAreaId: string;
  targetAreaId: string;
  relationshipType: 'data_flow' | 'approval' | 'reporting' | 'resource_sharing' | 'dependency' | 'custom';
  label: string;
  description: string | null;
  weight: number;
  isCritical: boolean;
  integrationDetails: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  sourceAreaKey: string | null;
  targetAreaKey: string | null;
}

export interface IntegrationMap {
  nodes: IntegrationNode[];
  edges: IntegrationEdge[];
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// ── API Methods ─────────────────────────────────────────────────────

const BASE = '/api/tenant-portal/integration-map';

export async function fetchFullMap(
  authFetch: AuthFetch,
  tenantId?: string,
): Promise<IntegrationMap> {
  const url = tenantId ? `${BASE}?tenant_id=${tenantId}` : BASE;
  const res = await authFetch(url);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to load integration map: ${res.status} ${body}`);
  }
  return res.json();
}

export async function updateNodePosition(
  authFetch: AuthFetch,
  nodeId: string,
  x: number,
  y: number,
  tenantId?: string,
): Promise<void> {
  const url = tenantId
    ? `${BASE}/nodes/${nodeId}?tenant_id=${tenantId}`
    : `${BASE}/nodes/${nodeId}`;
  const res = await authFetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ xPosition: x, yPosition: y }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to save node position: ${res.status} ${body}`);
  }
}
